<template>
  <div class="job-detail" v-loading="loading">
    <template v-if="dataInfo.id">
      <!-- 顶栏：鉴别号、订单、状态 -->
      <div class="hero">
        <div class="hero-left">
          <div class="hero-no">{{ dataInfo.inspectionNo || "—" }}</div>
          <div class="hero-sub">
            <span>{{ getDictLabel("orderTypeOptions", dataInfo.orderType) }}</span>
            <span class="sep">|</span>
            <span class="mono">{{ dataInfo.orderNo || "—" }}</span>
            <template v-if="dataInfo.shelfNo">
              <span class="sep">|</span>
              <span>货架 {{ dataInfo.shelfNo }}</span>
            </template>
          </div>
          <div class="hero-time" v-if="timeLineText">{{ timeLineText }}</div>
        </div>
        <div class="hero-right">
          <el-tag :type="statusTagType" effect="dark" size="small">{{ getDictLabel("statusOptions", dataInfo.status) }}</el-tag>
          <el-tag size="small" effect="plain">{{ platformInspectionTypeLabel(dataInfo.inspectionType) }}</el-tag>
          <el-tag v-if="dataInfo.inspectionNoPrintFlag === 1" type="success" size="mini" effect="plain">已打印</el-tag>
        </div>
      </div>

      <!-- 鉴别结论（有任一结论时展示） -->
      <div class="result-row" v-if="hasResultStrip">
        <div class="result-cell" v-if="dataInfo.resultAppearance">
          <span class="result-label">外观</span>
          <span class="result-val" :style="{ color: getDictColor('passFailResultOptions', dataInfo.resultAppearance) }">
            {{ getDictLabel("passFailResultOptions", dataInfo.resultAppearance) }}
          </span>
        </div>
        <div class="result-cell" v-if="showLiquidResultInStrip">
          <span class="result-label">液体</span>
          <span class="result-val">
            <span :style="{ color: getDictColor('passFailResultOptions', dataInfo.resultLiquid) }">
              {{ getDictLabel("passFailResultOptions", dataInfo.resultLiquid) }}
            </span>
            <span v-if="dataInfo.ychl" class="result-sub">（乙醇 {{ dataInfo.ychl }}）</span>
          </span>
        </div>
        <div class="result-cell" v-else-if="showLiquidPendingInStrip">
          <span class="result-label">液体</span>
          <span class="result-val result-val--muted">未鉴别</span>
        </div>
        <div class="result-cell" v-if="dataInfo.result">
          <span class="result-label">鉴别结果</span>
          <span class="result-val" :style="{ color: getDictColor('passFailResultOptions', dataInfo.result) }">
            {{ getDictLabel("passFailResultOptions", dataInfo.result) }}
          </span>
        </div>
        <div class="result-cell" v-if="dataInfo.gradeLevel">
          <span class="result-label">评级</span>
          <span class="result-val">{{ dataInfo.gradeLevel }}<span v-if="dataInfo.gradeTotalScore != null" class="result-sub"> / {{ dataInfo.gradeTotalScore }}分</span></span>
        </div>
      </div>

      <!-- 商品 -->
      <section class="block" v-if="hasProductBlock">
        <div class="block-title">商品</div>
        <div class="product-box">
          <div class="img-list" v-if="productImages.length">
            <el-image
              v-for="(url, idx) in productImages"
              :key="'p-' + idx"
              class="img-item"
              :src="url"
              fit="cover"
              :preview-src-list="productImages"
            />
          </div>
          <div class="product-text">
            <div class="product-title" v-if="dataInfo.productTitle">{{ dataInfo.productTitle }}</div>
            <ul class="kv-list">
              <li v-if="brandText"><span class="k">品牌</span><span class="v">{{ brandText }}</span></li>
              <li v-if="typeText"><span class="k">型号</span><span class="v">{{ typeText }}</span></li>
              <li><span class="k">批次</span><span class="v">{{ dataInfo.batchNum || "—" }}</span></li>
              <li v-if="dataInfo.batchValidDays != null"><span class="k">批次有效</span><span class="v">{{ dataInfo.batchValidDays }} 天</span></li>
              <li><span class="k">分装</span><span class="v">{{ sffzLabel(dataInfo.sffz) }}</span></li>
              <li v-if="specMl"><span class="k">规格</span><span class="v">{{ specMl }}</span></li>
            </ul>
          </div>
        </div>
      </section>

      <!-- 备注 -->
      <section class="block" v-if="dataInfo.orderDepict || dataInfo.adminBz">
        <div class="block-title">备注</div>
        <div class="remark-line" v-if="dataInfo.orderDepict">
          <span class="k">用户</span><span class="v">{{ dataInfo.orderDepict }}</span>
        </div>
        <div class="remark-line" v-if="dataInfo.adminBz">
          <span class="k">管理员</span><span class="v">{{ dataInfo.adminBz }}</span>
        </div>
      </section>

      <!-- 外观 -->
      <section class="block" v-if="showAppearanceSection">
        <div class="block-title">外观鉴别</div>
        <div class="img-list" v-if="realImg.length">
          <div class="img-group-label">实物图</div>
          <div class="img-row">
            <el-image v-for="(item, i) in realImg" :key="'r' + i" class="img-item" :src="item" fit="cover" :preview-src-list="realImg" />
          </div>
        </div>
        <div class="img-list" v-if="cxImg.length">
          <div class="img-group-label">瑕疵图</div>
          <div class="img-row">
            <el-image v-for="(item, i) in cxImg" :key="'c' + i" class="img-item" :src="item" fit="cover" :preview-src-list="cxImg" />
          </div>
        </div>
        <p v-if="!hasAppearancePictures && !dataInfo.resultAppearance" class="empty-tip">暂无外观图片与结论</p>
      </section>

      <!-- 液体 -->
      <section class="block" v-if="showLiquidSection">
        <div class="block-title">液体鉴别</div>
        <el-image
          v-if="dataInfo.jbtpUrl"
          class="spectrum"
          :src="dataInfo.jbtpUrl"
          fit="contain"
          :preview-src-list="[dataInfo.jbtpUrl]"
        />
        <p v-else class="empty-tip">暂无鉴别图谱</p>
      </section>

      <!-- 报告 -->
      <section class="block" v-if="dataInfo.inspectionReportUrl || (showResultSection && dataInfo.remainDesc)">
        <div class="block-title">报告与余量</div>
        <p v-if="dataInfo.remainDesc" class="remain-desc"><span class="k">余量</span>{{ dataInfo.remainDesc }}</p>
        <el-image
          v-if="dataInfo.inspectionReportUrl"
          class="report"
          :src="dataInfo.inspectionReportUrl"
          fit="contain"
          :preview-src-list="[dataInfo.inspectionReportUrl]"
        />
      </section>

      <!-- 物流 / 回寄 -->
      <section class="block block--inline" v-if="showLogisticsSection || showReceiverSection">
        <div class="inline-pair" v-if="showLogisticsSection">
          <span class="k">物流</span>
          <span class="v">{{ dataInfo.logisticsCompany || "—" }} {{ dataInfo.logisticsNo || "" }}</span>
        </div>
        <div class="inline-pair" v-if="showReceiverSection">
          <span class="k">回寄</span>
          <span class="v">{{ receiverLine }}</span>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import { api } from "@/util/requestUrl";
import {
  brandDisplay,
  formatInspectionDateTime,
  parsePictureUrls,
  platformInspectionTypeLabel,
  productImageUrls,
  sffzLabel,
  specificationMl,
  typeDisplay,
} from "@/util/inspectionJobDisplay";

export default {
  name: "InspectionJobDetail",
  props: {
    jobId: { type: [String, Number], required: true },
    previewRow: { type: Object, default: null },
  },
  data() {
    return {
      loading: false,
      dataInfo: {},
      realImg: [],
      cxImg: [],
      dicts: {
        statusOptions: [
          { value: 1, label: "平台鉴别中", color: "#E6A23C", tag: "warning" },
          { value: 2, label: "待鉴别结果出具", color: "#409EFF", tag: "primary" },
          { value: 3, label: "待平台寄售评级", color: "#409EFF", tag: "info" },
          { value: 4, label: "待发货", color: "#67C23A", tag: "success" },
          { value: 5, label: "已完成", color: "#909399", tag: "info" },
        ],
        orderTypeOptions: [
          { value: 1, label: "寄售订单" },
          { value: 2, label: "鉴别订单" },
          { value: 3, label: "闲置订单" },
          { value: 4, label: "安心购订单" },
        ],
        passFailResultOptions: [
          { value: 1, label: "通过", color: "#67C23A" },
          { value: 2, label: "不通过", color: "#F56C6C" },
        ],
      },
    };
  },
  computed: {
    statusTagType() {
      const map = { 1: "warning", 2: "", 3: "info", 4: "success", 5: "info" };
      return map[Number(this.dataInfo.status)] || "info";
    },
    productImages() {
      return productImageUrls(this.dataInfo);
    },
    brandText() {
      return brandDisplay(this.dataInfo);
    },
    typeText() {
      return typeDisplay(this.dataInfo);
    },
    specMl() {
      return specificationMl(this.dataInfo.specification);
    },
    timeLineText() {
      const end = this.dataInfo.inspectionEndTime;
      const start = this.dataInfo.inspectionStartTime || this.dataInfo.createTime;
      if (end) return "完成于 " + formatInspectionDateTime(end);
      if (start) return "开始于 " + formatInspectionDateTime(start);
      return "";
    },
    receiverLine() {
      const parts = [
        this.dataInfo.receiverName,
        this.dataInfo.receiverPhone,
        this.dataInfo.receiverAddress,
      ].filter(Boolean);
      return parts.join("，") || "—";
    },
    hasResultStrip() {
      return !!(
        this.dataInfo.resultAppearance ||
        this.dataInfo.resultLiquid ||
        this.dataInfo.result ||
        this.dataInfo.gradeLevel
      );
    },
    showLiquidSection() {
      const t = Number(this.dataInfo.inspectionType);
      return t === 1 || t === 2 || this.dataInfo.jbtpUrl || this.dataInfo.resultLiquid || this.dataInfo.ychl;
    },
    /** 已实际上传图谱/乙醇，可展示液体结论 */
    liquidInspectionDone() {
      return !!(this.dataInfo.jbtpUrl || this.dataInfo.ychl);
    },
    showLiquidResultInStrip() {
      if (!this.showLiquidSection) return false;
      const rl = Number(this.dataInfo.resultLiquid);
      if (rl !== 1 && rl !== 2) return false;
      return this.liquidInspectionDone;
    },
    /** 外观+液体档，外观不通过导致液体未做 */
    showLiquidPendingInStrip() {
      if (!this.showLiquidSection) return false;
      const t = Number(this.dataInfo.inspectionType);
      if (t !== 1 && t !== 2) return false;
      if (Number(this.dataInfo.resultAppearance) !== 2) return false;
      return !this.liquidInspectionDone;
    },
    showAppearanceSection() {
      const t = Number(this.dataInfo.inspectionType);
      return t === 3 || this.hasAppearancePictures || this.dataInfo.resultAppearance;
    },
    showLogisticsSection() {
      return !!(this.dataInfo.logisticsCompany || this.dataInfo.logisticsNo);
    },
    showReceiverSection() {
      return !!(this.dataInfo.receiverName || this.dataInfo.receiverPhone || this.dataInfo.receiverAddress);
    },
    showResultSection() {
      return Number(this.dataInfo.status) > 1;
    },
    hasProductBlock() {
      return !!(
        this.dataInfo.productTitle ||
        this.productImages.length ||
        this.brandText ||
        this.typeText
      );
    },
    hasAppearancePictures() {
      return this.realImg.length > 0 || this.cxImg.length > 0;
    },
  },
  watch: {
    jobId: {
      immediate: true,
      handler(id) {
        if (id) this.loadData();
      },
    },
  },
  methods: {
    platformInspectionTypeLabel,
    sffzLabel,
    applyRow(row) {
      if (!row) return;
      this.dataInfo = { ...row };
      this.realImg = parsePictureUrls(row.realpicture);
      this.cxImg = parsePictureUrls(row.defectImgs);
    },
    loadData() {
      if (this.previewRow) this.applyRow(this.previewRow);
      this.loading = true;
      this.$axios
        .get(api.getInspectionJobById, { params: { id: this.jobId } })
        .then((res) => {
          if (res.data.code === 200 && res.data.data) {
            const d = res.data.data;
            this.dataInfo = d;
            this.realImg = parsePictureUrls(d.realpicture);
            this.cxImg = parsePictureUrls(d.defectImgs);
            this.$emit("loaded", d);
          } else {
            this.$message.error(res.data.msg || "获取详情失败");
          }
        })
        .catch(() => this.$message.error("网络异常"))
        .finally(() => {
          this.loading = false;
        });
    },
    getDictLabel(dictKey, value) {
      if (value == null) return "—";
      const item = (this.dicts[dictKey] || []).find((d) => d.value === value);
      return item ? item.label : "—";
    },
    getDictColor(dictKey, value) {
      const item = (this.dicts[dictKey] || []).find((d) => d.value === value);
      return item ? item.color : "#303133";
    },
  },
};
</script>

<style scoped>
.job-detail {
  padding: 0 8px 16px;
  min-height: 100px;
  color: #303133;
  font-size: 14px;
}

/* 顶栏 */
.hero {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  padding-bottom: 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid #ebeef5;
}

.hero-no {
  font-size: 18px;
  font-weight: 600;
  letter-spacing: 0.5px;
  color: #303133;
  line-height: 1.4;
}

.hero-sub {
  margin-top: 6px;
  font-size: 13px;
  color: #606266;
}

.hero-sub .mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
}

.hero-sub .sep {
  margin: 0 8px;
  color: #dcdfe6;
}

.hero-time {
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
}

.hero-right {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-end;
  flex-shrink: 0;
}

/* 结论条 */
.result-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 24px;
  padding: 12px 16px;
  margin-bottom: 20px;
  background: #fafafa;
  border-radius: 6px;
  border: 1px solid #f0f0f0;
}

.result-cell {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.result-label {
  font-size: 12px;
  color: #909399;
  flex-shrink: 0;
}

.result-val {
  font-size: 14px;
  font-weight: 600;
}

.result-val--muted {
  font-weight: normal;
  color: #c0c4cc;
}

.result-sub {
  font-size: 12px;
  font-weight: normal;
  color: #909399;
}

/* 分区 */
.block {
  margin-bottom: 22px;
}

.block-title {
  font-size: 13px;
  font-weight: 600;
  color: #909399;
  margin-bottom: 10px;
  padding-left: 8px;
  border-left: 3px solid #409eff;
}

.block--inline .inline-pair {
  margin-bottom: 8px;
  font-size: 13px;
  line-height: 1.6;
}

.block--inline .k {
  display: inline-block;
  width: 40px;
  color: #909399;
}

.product-box {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.product-title {
  font-size: 15px;
  font-weight: 500;
  margin-bottom: 10px;
  line-height: 1.5;
}

.kv-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.kv-list li {
  display: flex;
  margin-bottom: 6px;
  font-size: 13px;
  line-height: 1.5;
}

.kv-list .k {
  width: 56px;
  flex-shrink: 0;
  color: #909399;
}

.kv-list .v {
  flex: 1;
  color: #606266;
}

.remark-line {
  font-size: 13px;
  line-height: 1.6;
  margin-bottom: 6px;
}

.remark-line .k {
  color: #909399;
  margin-right: 8px;
}

.remark-line .v {
  color: #606266;
}

.img-list {
  margin-bottom: 10px;
}

.img-group-label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 6px;
}

.img-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.img-item {
  width: 88px;
  height: 88px;
  border-radius: 4px;
  overflow: hidden;
  background: #f5f7fa;
}

.spectrum,
.report {
  max-width: 100%;
  max-height: 320px;
  border-radius: 4px;
  background: #f5f7fa;
}

.remain-desc {
  margin: 0 0 10px;
  font-size: 13px;
  color: #606266;
}

.remain-desc .k {
  color: #909399;
  margin-right: 8px;
}

.empty-tip {
  margin: 0;
  font-size: 13px;
  color: #c0c4cc;
}
</style>



// WEBPACK FOOTER //
// src/pages/inspectionJob/detail.vue